import React, { useState } from 'react';
import { Check, Clock, Download, FilePlus, RefreshCw, Shield, Truck, Zap } from 'lucide-react';
import { useDirection } from '@/contexts/DirectionContext';
import type { ContainerStatusId } from '../constants/statuses';
import { documentsApi, downloadBase64Pdf, type PortalDocumentType } from '../api/documents.api';

/**
 * Documentele unui conosament (B/L): avizarea de sosire, cererea de eliberare și LOI-ul.
 * Se generează în aplicația principală, pe `billId` — de aceea le arătăm în modalul de B/L,
 * iar în modalul de container doar ca scurtătură, cu B/L-ul ales explicit.
 */
type DocState = 'loading' | 'done' | 'error' | undefined;

interface BillDocumentsPanelProps {
  billId: number | null;
  /** Telex / SWB / OBL — LOI-ul există doar pentru Telex și SWB. */
  releaseType: string | null;
  status: ContainerStatusId;
  lang: 'ro' | 'en';
}

export const BillDocumentsPanel: React.FC<BillDocumentsPanelProps> = ({ billId, releaseType, status, lang }) => {
  const isRo = lang === 'ro';
  const { direction } = useDirection();
  const [requested, setRequested] = useState<Record<string, DocState>>({});
  const [errorMsg, setErrorMsg] = useState<Record<string, string>>({});

  // LOI există doar pentru conosamente cu release Telex / SWB (în aplicația mare).
  const loiEligible = /^(telex|swb)$/i.test((releaseType || '').trim());

  const arrived = ['discharged', 'in_delivery', 'completed'].includes(status);
  // Ultima etapă înainte de sosire diferă pe direcție: pe import containerul e pe
  // feeder prin Marea Neagră (`black_sea`), pe export e deja pe nava mamă spre
  // destinație (`in_transit`); `transshipment` e penultima etapă în ambele cazuri.
  const nearArrival = (direction === 'export'
    ? ['transshipment', 'in_transit']
    : ['transshipment', 'black_sea']
  ).includes(status);

  const issued = [
    {
      key: 'arrival_notice',
      docType: 'arrivalUpdate' as PortalDocumentType,
      icon: <Truck size={20} strokeWidth={2} />,
      tone: 'info' as 'info' | 'success' | 'accent',
      titleRo: 'Avizare de sosire',
      titleEn: 'Arrival Update',
      descRo: 'Detalii despre sosirea bookingului în portul de destinație.',
      descEn: "Details about the booking's arrival at the destination port.",
      ready: arrived || nearArrival,
      pendingRo: 'Va fi emisă cu câteva zile înainte de sosire',
      pendingEn: 'Will be issued a few days before arrival',
    },
  ];

  const generatables = [
    {
      key: 'release_request',
      docType: 'releaseRequest' as PortalDocumentType,
      icon: <FilePlus size={19} strokeWidth={2.2} />,
      titleRo: 'Cerere de eliberare',
      titleEn: 'Release request',
      descRo: 'Solicită eliberarea containerului către o terță parte autorizată.',
      descEn: 'Request container release to an authorised third party.',
      ctaRo: 'Generează cerere',
      ctaEn: 'Generate request',
      doneRo: 'Document descărcat',
      doneEn: 'Document downloaded',
      disabled: false,
    },
    {
      key: 'loi',
      docType: 'loi' as PortalDocumentType,
      icon: <Shield size={19} strokeWidth={2.2} />,
      titleRo: 'Letter of Indemnity',
      titleEn: 'Letter of Indemnity',
      descRo: 'Scrisoare de garanție folosită când conosamentul original nu este disponibil.',
      descEn: 'Indemnity letter used when the original B/L is not available.',
      ctaRo: 'Generează LOI',
      ctaEn: 'Generate LOI',
      doneRo: 'Document descărcat',
      doneEn: 'Document downloaded',
      // LOI se generează doar pentru Telex / SWB.
      disabled: !loiEligible,
    },
  ];

  const handleGenerate = async (key: string, docType: PortalDocumentType) => {
    if (requested[key] === 'loading') return;
    if (!billId) {
      setRequested((r) => ({ ...r, [key]: 'error' }));
      setErrorMsg((m) => ({
        ...m,
        [key]: isRo ? 'Nu există conosament pentru care să generăm documentul.' : 'No bill of lading to generate the document for.',
      }));
      return;
    }
    setRequested((r) => ({ ...r, [key]: 'loading' }));
    setErrorMsg((m) => ({ ...m, [key]: '' }));
    try {
      const files = await documentsApi.generate(billId, docType);
      files.forEach((f) => downloadBase64Pdf(f));
      setRequested((r) => ({ ...r, [key]: 'done' }));
    } catch (err: any) {
      const apiMsg = err?.response?.data?.message as string | undefined;
      setRequested((r) => ({ ...r, [key]: 'error' }));
      setErrorMsg((m) => ({
        ...m,
        [key]: apiMsg || (isRo ? 'Nu s-a putut genera documentul.' : 'Could not generate the document.'),
      }));
    }
  };

  const sectionHead = (title: string, sub: string) => (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--text-subtle)',
          marginBottom: 3,
        }}
      >
        {title}
      </div>
      <div className="muted fs-12">{sub}</div>
    </div>
  );

  return (
    <div>
      {sectionHead(
        isRo ? 'Documente emise de TLS' : 'Issued by TLS',
        isRo
          ? 'Eliberate automat în funcție de etapa expediției.'
          : 'Released automatically based on the shipment stage.',
      )}

      <div style={{ display: 'grid', gap: 10, marginBottom: 28 }}>
        {issued.map((d) => {
          const ready = d.ready;
          const toneColor =
            d.tone === 'success'
              ? 'var(--st-success)'
              : d.tone === 'accent'
              ? 'var(--accent)'
              : 'var(--st-info)';
          const toneBg =
            d.tone === 'success'
              ? 'var(--st-success-bg)'
              : d.tone === 'accent'
              ? 'var(--accent-soft)'
              : 'var(--st-info-bg)';

          return (
            <div
              key={d.key}
              style={{
                padding: 14,
                borderRadius: 'var(--r-lg)',
                border: ready ? '1px solid var(--border)' : '1px dashed var(--border-strong)',
                background: ready ? 'var(--surface)' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                opacity: ready ? 1 : 0.72,
                transition: 'opacity 0.15s',
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 10,
                  background: ready ? toneBg : 'var(--surface-2)',
                  color: ready ? toneColor : 'var(--text-subtle)',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                {d.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
                  <div className="fw-600 fs-13" style={{ letterSpacing: '-0.005em' }}>
                    {isRo ? d.titleRo : d.titleEn}
                  </div>
                  {ready ? (
                    <span className="badge badge-success" style={{ fontSize: 10.5 }}>
                      <Check size={10} strokeWidth={3} />
                      {isRo ? 'Disponibil' : 'Available'}
                    </span>
                  ) : (
                    <span className="badge badge-neutral" style={{ fontSize: 10.5 }}>
                      <Clock size={10} strokeWidth={2.5} />
                      {isRo ? 'În așteptare' : 'Pending'}
                    </span>
                  )}
                </div>
                <div className="muted fs-12 mt-1" style={{ lineHeight: 1.4 }}>
                  {isRo ? d.descRo : d.descEn}
                </div>
                <div className="muted fs-11 mt-2">
                  {ready
                    ? isRo
                      ? 'Disponibil pentru descărcare'
                      : 'Available for download'
                    : isRo
                    ? d.pendingRo
                    : d.pendingEn}
                </div>
                {requested[d.key] === 'error' && errorMsg[d.key] && (
                  <div className="fs-11 mt-1" style={{ color: 'var(--st-danger, #dc2626)' }}>
                    {errorMsg[d.key]}
                  </div>
                )}
              </div>
              <div style={{ flexShrink: 0 }}>
                {ready ? (
                  <button
                    type="button"
                    className="btn btn-sm"
                    title={isRo ? 'Descarcă' : 'Download'}
                    disabled={requested[d.key] === 'loading'}
                    onClick={() => handleGenerate(d.key, d.docType)}
                  >
                    {requested[d.key] === 'loading' ? (
                      <RefreshCw size={13} strokeWidth={2.5} style={{ animation: 'spin 0.8s linear infinite' }} />
                    ) : (
                      <Download size={13} />
                    )}
                    <span className="hidden-mobile">
                      {requested[d.key] === 'loading'
                        ? isRo
                          ? 'Se generează…'
                          : 'Generating…'
                        : isRo
                        ? 'Descarcă'
                        : 'Download'}
                    </span>
                  </button>
                ) : (
                  <div
                    className="muted fs-11"
                    style={{
                      padding: '4px 8px',
                      border: '1px dashed var(--border-strong)',
                      borderRadius: 99,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    —
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {sectionHead(
        isRo ? 'Generează un document' : 'Generate a document',
        isRo
          ? 'Documente generate la cerere și descărcate ca PDF.'
          : 'Documents generated on request and downloaded as PDF.',
      )}

      <div
        style={{
          display: 'grid',
          gap: 10,
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        }}
      >
        {generatables.map((d) => {
          const state = requested[d.key];
          const isLoading = state === 'loading';
          const isDone = state === 'done';
          const isError = state === 'error';
          const isDisabled = d.disabled;

          return (
            <div
              key={d.key}
              style={{
                padding: 16,
                borderRadius: 'var(--r-lg)',
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                minHeight: 168,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: -30,
                  right: -30,
                  width: 110,
                  height: 110,
                  borderRadius: 99,
                  background: isDone ? 'var(--st-success-bg)' : 'var(--accent-soft)',
                  opacity: 0.5,
                  pointerEvents: 'none',
                }}
              />
              <div style={{ position: 'relative' }}>
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    background: isDone ? 'var(--st-success-bg)' : 'var(--accent-soft)',
                    color: isDone ? 'var(--st-success)' : 'var(--accent)',
                    display: 'grid',
                    placeItems: 'center',
                    marginBottom: 10,
                  }}
                >
                  {isDone ? <Check size={19} strokeWidth={2.2} /> : d.icon}
                </div>
                <div className="fw-600 fs-14" style={{ letterSpacing: '-0.01em' }}>
                  {isRo ? d.titleRo : d.titleEn}
                </div>
                <div className="muted fs-12 mt-1" style={{ lineHeight: 1.45 }}>
                  {isRo ? d.descRo : d.descEn}
                </div>
                {isDisabled && (
                  <div className="muted fs-11 mt-1" style={{ fontStyle: 'italic' }}>
                    {isRo
                      ? 'Disponibil doar pentru conosamente Telex / Sea Waybill.'
                      : 'Available only for Telex / Sea Waybill releases.'}
                  </div>
                )}
                {isError && errorMsg[d.key] && (
                  <div className="fs-11 mt-1" style={{ color: 'var(--st-danger, #dc2626)' }}>
                    {errorMsg[d.key]}
                  </div>
                )}
              </div>
              <div style={{ marginTop: 'auto', position: 'relative' }}>
                {isDone ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 10px',
                      background: 'var(--st-success-bg)',
                      color: 'var(--st-success)',
                      borderRadius: 'var(--r-md)',
                      fontSize: 12.5,
                      fontWeight: 500,
                    }}
                  >
                    <Check size={14} strokeWidth={2.5} />
                    {isRo ? d.doneRo : d.doneEn}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => handleGenerate(d.key, d.docType)}
                    disabled={isLoading || isDisabled}
                    style={{
                      width: '100%',
                      justifyContent: 'center',
                      padding: '8px 14px',
                      opacity: isLoading || isDisabled ? 0.6 : 1,
                      cursor: isDisabled ? 'not-allowed' : undefined,
                    }}
                  >
                    {isLoading ? (
                      <>
                        <RefreshCw
                          size={13}
                          strokeWidth={2.5}
                          style={{ animation: 'spin 0.8s linear infinite' }}
                        />
                        {isRo ? 'Se generează…' : 'Generating…'}
                      </>
                    ) : (
                      <>
                        <Zap size={13} strokeWidth={2.5} />
                        {isRo ? d.ctaRo : d.ctaEn}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
